// Stripe Settings Component - Configure payment processing
import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Check, ExternalLink, Eye, EyeOff, CreditCard, Loader, RefreshCw } from 'lucide-react';
import settingsService from '../../services/settingsService';

const StripeSettings = () => {
  const [settings, setSettings] = useState({
    stripe_enabled: false,
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    stripe_mode: 'test' // 'test' or 'live'
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasChanges, setHasChanges] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsService.getSettings();
      console.log('Stripe settings response:', response);

      // Handle both direct response and nested data
      const data = response.data || response;

      const stripeSettings = {
        stripe_enabled: data?.stripe_enabled || false,
        stripe_publishable_key: data?.stripe_publishable_key || '',
        stripe_secret_key: data?.stripe_secret_key || '',
        stripe_webhook_secret: data?.stripe_webhook_secret || '',
        stripe_mode: data?.stripe_mode || 'test'
      };
      setSettings(stripeSettings);
      setOriginalSettings(stripeSettings);
    } catch (error) {
      console.error('Error fetching Stripe settings:', error);
      setMessage({ type: 'error', text: 'Failed to load Stripe settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const testStripeConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await fetch('/api/payments/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('classtraks_token')}`
        },
        body: JSON.stringify({
          publishable_key: settings.stripe_publishable_key,
          secret_key: settings.stripe_secret_key
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTestResult({ success: true, message: 'Stripe connection successful!' });
      } else {
        setTestResult({ success: false, message: data.error || 'Connection failed' });
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Failed to test connection' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await settingsService.updateSettings(settings);
      setOriginalSettings(settings);
      setMessage({ type: 'success', text: 'Stripe settings saved successfully!' });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving Stripe settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save Stripe settings' });
    } finally {
      setSaving(false);
    }
  };

  const isConfigured = settings.stripe_publishable_key && settings.stripe_secret_key;
  const isTestMode = settings.stripe_mode === 'test';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center space-x-3">
            <CreditCard className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Stripe Payment Settings</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Configure Stripe to accept payments for class enrollments</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading Stripe settings...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center space-x-3">
          <CreditCard className="w-8 h-8 text-[#7A99D8]" />
          <div>
            <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Stripe Payment Settings</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Configure Stripe to accept payments for class enrollments</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Status Badges */}
        <div className="flex items-center space-x-2 mb-6">
        {isConfigured && (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            settings.stripe_enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {settings.stripe_enabled ? 'Enabled' : 'Disabled'}
          </span>
        )}
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          isTestMode
            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
        }`}>
          {isTestMode ? 'Test Mode' : 'Live Mode'}
        </span>
      </div>

      {/* Setup Guide for New Users */}
      {!isConfigured && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-4 flex items-center">
            <CreditCard className="w-5 h-5 mr-2" />
            Getting Started with Stripe
          </h3>
          <div className="space-y-3 text-sm text-blue-800 dark:text-blue-300">
            <p>Follow these steps to set up payment processing:</p>
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>
                Create a Stripe account at{' '}
                <a
                  href="https://stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:text-blue-600 inline-flex items-center"
                >
                  stripe.com
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>Complete your business profile and bank account verification</li>
              <li>
                Get your API keys from the{' '}
                <a
                  href="https://dashboard.stripe.com/test/apikeys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:text-blue-600 inline-flex items-center"
                >
                  Stripe Dashboard
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>Copy and paste your keys below</li>
              <li>Test your integration with Stripe test cards</li>
              <li>Switch to live mode when ready to accept real payments</li>
            </ol>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {message.text && (
        <div className={`rounded-lg p-4 ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            )}
            <span className={message.type === 'success' ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}>
              {message.text}
            </span>
          </div>
        </div>
      )}

      {/* Test Connection Result */}
      {testResult && (
        <div className={`rounded-lg p-4 ${
          testResult.success
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center">
            {testResult.success ? (
              <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            )}
            <span className={testResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}>
              {testResult.message}
            </span>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">

        {/* Enable/Disable Stripe */}
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              name="stripe_enabled"
              id="stripe_enabled"
              checked={settings.stripe_enabled}
              onChange={handleInputChange}
              disabled={!isConfigured}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
          <div className="ml-3">
            <label htmlFor="stripe_enabled" className="font-medium text-gray-900 dark:text-white">
              Enable Stripe Payments
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Allow students to pay for classes using credit/debit cards
              {!isConfigured && (
                <span className="text-orange-600 dark:text-orange-400"> (Configure API keys first)</span>
              )}
            </p>
          </div>
        </div>

        {/* Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Stripe Mode
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="stripe_mode"
                value="test"
                checked={settings.stripe_mode === 'test'}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Test Mode (for testing)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="stripe_mode"
                value="live"
                checked={settings.stripe_mode === 'live'}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Live Mode (real payments)</span>
            </label>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {isTestMode
              ? 'Use test mode to try out payments without processing real transactions'
              : 'Live mode processes real payments and charges real cards'}
          </p>
        </div>

        {/* Publishable Key */}
        <div>
          <label htmlFor="stripe_publishable_key" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Publishable Key *
          </label>
          <input
            type="text"
            name="stripe_publishable_key"
            id="stripe_publishable_key"
            value={settings.stripe_publishable_key}
            onChange={handleInputChange}
            placeholder={isTestMode ? 'pk_test_...' : 'pk_live_...'}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Your {isTestMode ? 'test' : 'live'} publishable key from Stripe Dashboard → API keys
          </p>
        </div>

        {/* Secret Key */}
        <div>
          <label htmlFor="stripe_secret_key" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Secret Key *
          </label>
          <div className="relative">
            <input
              type={showSecretKey ? 'text' : 'password'}
              name="stripe_secret_key"
              id="stripe_secret_key"
              value={settings.stripe_secret_key}
              onChange={handleInputChange}
              placeholder={isTestMode ? 'sk_test_...' : 'sk_live_...'}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowSecretKey(!showSecretKey)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Your {isTestMode ? 'test' : 'live'} secret key - keep this confidential!
          </p>
        </div>

        {/* Webhook Secret (Optional) */}
        <div>
          <label htmlFor="stripe_webhook_secret" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Webhook Secret (Optional)
          </label>
          <div className="relative">
            <input
              type={showWebhookSecret ? 'text' : 'password'}
              name="stripe_webhook_secret"
              id="stripe_webhook_secret"
              value={settings.stripe_webhook_secret}
              onChange={handleInputChange}
              placeholder="whsec_..."
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowWebhookSecret(!showWebhookSecret)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Required for webhooks - get this from Stripe Dashboard → Webhooks
          </p>
        </div>

        {/* Test Connection Button */}
        {isConfigured && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={testStripeConnection}
              disabled={testing || !settings.stripe_publishable_key || !settings.stripe_secret_key}
              className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Testing Connection...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Test Connection
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Test Cards Info (Test Mode Only) */}
      {isTestMode && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-3">Stripe Test Cards</h3>
          <div className="text-xs text-yellow-800 dark:text-yellow-300 space-y-2">
            <p>Use these test card numbers to try payments:</p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="bg-white dark:bg-gray-800 p-2 rounded border border-yellow-300 dark:border-yellow-700">
                <code className="text-xs font-mono">4242 4242 4242 4242</code>
                <span className="block text-xs text-gray-600 dark:text-gray-400">Success</span>
              </div>
              <div className="bg-white dark:bg-gray-800 p-2 rounded border border-yellow-300 dark:border-yellow-700">
                <code className="text-xs font-mono">4000 0000 0000 0002</code>
                <span className="block text-xs text-gray-600 dark:text-gray-400">Declined</span>
              </div>
            </div>
            <p className="mt-2">Use any future expiry date, any 3-digit CVC, and any 5-digit ZIP code.</p>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {hasChanges && 'You have unsaved changes'}
        </p>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
            hasChanges
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </button>
      </div>
      </div>
    </div>
  );
};

export default StripeSettings;
